import { ChatIcon, TipoConta } from 'components/Icons';
import VideoPlayer from 'components/__ASSOCIADO__/Associado/VideoPlayer';
import DuvidasFrequentes from 'components/__ASSOCIADO__/DuvidasFrequentes';
import PageSubtitle from 'components/__ASSOCIADO__/PageSubtitle/PageSubtitle';
import AssembleiaDateTime from 'components/__SHARED__/AssembleiaDateTime';
import LabelValueCard from 'components/__SHARED__/LabelValueCard/LabelValueCard';
import { parseISO } from 'date-fns';
import {
  AssembleiaPautaStatus,
  TipoVotoPauta,
} from 'helpers/enums/AssembleiaPautaStatus';
import { AssembleiaStatus } from 'helpers/enums/AssembleiaStatus';
import translation from 'locales/translation';

import { groupByLength } from 'helpers/utils/ArrayUtils';
import {
  getTotalContasSelecionadasOuNaoPeloUsuario,
  getTotalContasSelecionadasPeloUsuario,
} from 'helpers/utils/AssembleiaUtils';
import { clsx, dispatchToAllYouTubeVideos } from 'helpers/utils/HTMLUtils';
import { FixedElements } from 'models/layout.model';
import {
  Col,
  ComunidadeIcon,
  ElementPaginator,
  EvidenciasIcon,
  ListIcon,
  PlayIcon,
  Row,
  ScreenSize,
  Title,
  useCarouselBehaviour,
  useScreenSize,
} from 'plataforma-fundacao-componentes';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link as LinkScroll } from 'react-scroll';
import { IAAssociadoEventVideo } from 'store/modules/AssembleiasAssociadoReducer';
import BotaoFlutuante from './components/BotaoFlutuante';
import { Progress } from './components/Progress';
import Context from './Context';
import styles from './styles.module.scss';

export default function Assembleia() {
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const {
    currentEvent,
    usuario,
    loadingEvent,
    loadingPautas,
    hasPermissionVideos,
    hasPermissionDocumentos,
    hasPermissionDuvidas,
    abrirModalContas,
    abrirModalContasConfirmadas,
    abrirModalMenorEmancipado,
    abrirDocumentos,
    abrirVideos,
    isColaborador,
  } = useContext(Context);

  const contadorContas = useMemo(() => {
    if (currentEvent?.contasDisponiveis && usuario) {
      const contasSelecionadas = getTotalContasSelecionadasPeloUsuario(
        currentEvent.contasDisponiveis,
        usuario.cpfCnpj
      );

      const contasTotais =
        currentEvent.flagMenorSemEmancipado ||
        currentEvent.flagPresencaNaoOnline
          ? getTotalContasSelecionadasOuNaoPeloUsuario(
              currentEvent?.contasDisponiveis?.filter(
                (x) => typeof x.tipoPresenca === 'string'
              ) || [],
              usuario.cpfCnpj
            )
          : getTotalContasSelecionadasOuNaoPeloUsuario(
              currentEvent.contasDisponiveis,
              usuario.cpfCnpj
            );

      return `${contasSelecionadas}/${contasTotais}`;
    }

    return '-';
  }, [currentEvent, usuario]);

  const countVotos = currentEvent?.pautas
    ?.filter((pautas) => {
      return pautas.votos.length > 0;
    })
    .map((item) => {
      return item.votos;
    });

  const pautasResults = currentEvent?.pautas?.map((pautas) => {
    return {
      titulo: pautas.titulo,
      status: pautas.status,
      votos: pautas.votos,
      tipoVoto: pautas.tipoVotoPauta,
    };
  });

  const votosPermitidos = pautasResults?.filter((x) => {
    return x.tipoVoto === TipoVotoPauta.Vota;
  });

  const contadorVotos = useMemo(() => {
    if (currentEvent?.pautas && usuario) {
      return (
        (countVotos?.length! <= 9
          ? '0' + countVotos?.length
          : countVotos?.length) +
        '/' +
        (votosPermitidos?.length! <= 9
          ? '0' + votosPermitidos?.length
          : votosPermitidos?.length)
      );
    }
    return '-';
  }, [countVotos, currentEvent, usuario, votosPermitidos]);

  const liberadaSemVoto = pautasResults
    ?.filter((x) => x.tipoVoto === TipoVotoPauta.Vota)
    .filter((x) => {
      return x.status === AssembleiaPautaStatus.Liberada && !x.votos.length;
    });

  const nadaLiberadaSemVotoBloqueada = pautasResults
    ?.filter((x) => {
      return x.tipoVoto === TipoVotoPauta.Vota;
    })
    .filter((x) => {
      return (
        x.status !== AssembleiaPautaStatus.Liberada ||
        (x.status === AssembleiaPautaStatus.Liberada && x.votos.length)
      );
    });

  const verificarNadaLiberada = nadaLiberadaSemVotoBloqueada?.filter(
    (x: { status: AssembleiaPautaStatus; votos: Array<any> }) => {
      return x.status === AssembleiaPautaStatus.Liberada && x.votos.length;
    }
  );

  const todasEncerradasELiberadasComVoto = pautasResults?.every((x) => {
    return (
      x.status === AssembleiaPautaStatus.Encerrada ||
      (x.status === AssembleiaPautaStatus.Liberada && x.votos.length) ||
      (x.status === AssembleiaPautaStatus.Liberada &&
        !x.votos.length &&
        (x.tipoVoto === TipoVotoPauta.Restrito ||
          x.tipoVoto === TipoVotoPauta.Colaborador)) ||
      (x.status === AssembleiaPautaStatus.Bloqueada &&
        !x.votos.length &&
        (x.tipoVoto === TipoVotoPauta.Restrito ||
          x.tipoVoto === TipoVotoPauta.Colaborador))
    );
  });

  const naoMostrarBanner = pautasResults?.every((x) => {
    return (
      (x.status === AssembleiaPautaStatus.Encerrada && !x.votos.length) ||
      x.status === AssembleiaPautaStatus.Bloqueada
    );
  });

  const pautaConselheiro = pautasResults?.filter((x) => {
    return x.status === AssembleiaPautaStatus.Liberada;
  });

  const conselheiroNaoVota = pautaConselheiro?.every((x) => {
    return (
      x.tipoVoto === TipoVotoPauta.Restrito ||
      x.tipoVoto === TipoVotoPauta.Colaborador ||
      x.tipoVoto === TipoVotoPauta.SemPresenca ||
      x.tipoVoto === TipoVotoPauta.QuorumOutroRepresentante
    );
  });

  const pautaConselheiro2 = pautasResults?.filter((x) => {
    return (
      x.status === AssembleiaPautaStatus.Liberada ||
      x.status === AssembleiaPautaStatus.Encerrada
    );
  });

  const conselheiroNaoVota2 = pautaConselheiro2
    ?.filter((x) => {
      return x.tipoVoto === TipoVotoPauta.Vota;
    })
    .every((x) => {
      return x.status === AssembleiaPautaStatus.Encerrada && !x.votos.length;
    });

  const titleLabels = useCallback(() => {
    if (liberadaSemVoto?.length! >= 1) {
      return translation.AssembleiaAssociadoRoute.pautas.acompanhamento
        .emAndamento;
    } else if (
      (todasEncerradasELiberadasComVoto && !liberadaSemVoto?.length) ||
      countVotos?.length === votosPermitidos?.length
    ) {
      return translation.AssembleiaAssociadoRoute.pautas.acompanhamento
        .votacaoConcluida;
    } else if (
      (nadaLiberadaSemVotoBloqueada?.length! >= 1 ||
        verificarNadaLiberada!.length) &&
      !todasEncerradasELiberadasComVoto
    ) {
      return translation.AssembleiaAssociadoRoute.pautas.acompanhamento
        .emAndamento;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEvent?.pautas]);

  const descriptionLabels = useCallback(() => {
    if (liberadaSemVoto?.length! >= 1) {
      return translation.AssembleiaAssociadoRoute.pautas.acompanhamento
        .emAndamentoDescription;
    } else if (
      (todasEncerradasELiberadasComVoto && !liberadaSemVoto?.length) ||
      countVotos?.length === votosPermitidos?.length
    ) {
      return translation.AssembleiaAssociadoRoute.pautas.acompanhamento
        .votacaoConcluidaDescription;
    } else if (
      (nadaLiberadaSemVotoBloqueada!.length >= 1 ||
        verificarNadaLiberada?.length) &&
      !todasEncerradasELiberadasComVoto
    ) {
      return translation.AssembleiaAssociadoRoute.pautas.acompanhamento
        .aguardeDescription;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEvent?.pautas]);

  const video = currentEvent?.urlTransmissao;
  const watchToEmbed = video?.replace('/watch?v=', '/embed/');

  useEffect(() => {
    if (
      localStorage.getItem('notificacaoChat') &&
      localStorage.getItem('badgeConfig')
    ) {
      localStorage.removeItem('notificacaoChat');
      localStorage.removeItem('badgeConfig');
    }
    setTimeout(() => {
      localStorage.setItem(
        'notificacaoChat',
        JSON.stringify({
          somenteMinhasNotificacoes: window.matchMedia('(max-width: 959px)')
            .matches
            ? false
            : true,
        })
      );
    }, 1000);

    return () => {
      localStorage.removeItem('notificacaoChat');
      localStorage.removeItem('badgeConfig');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [atual, setAtual] = useState<number>(0);
  const { rankedSize: screenSize } = useScreenSize();

  const handleVideoStarted = useCallback((id: string) => {
    dispatchToAllYouTubeVideos('pauseVideo', id);
  }, []);

  const carouselVideos = useMemo(() => {
    return groupByLength(
      currentEvent?.videos!,
      screenSize >= ScreenSize.lg ? 4 : screenSize === ScreenSize.md ? 2 : 1
    ).map((i: IAAssociadoEventVideo[]) =>
      i.map((x: IAAssociadoEventVideo) => {
        return (
          <div
            className={styles.card_videos}
            key={`carousel-video-${x.oidVideo}`}
          >
            <VideoPlayer
              url={x.urlYoutube}
              callBackStarted={handleVideoStarted}
              blockRedirect={true}
            />

            <p>{x.descricao}</p>
          </div>
        );
      })
    );
  }, [currentEvent?.videos, handleVideoStarted, screenSize]);

  const [CarouselBehaviour, handleChange] = useCarouselBehaviour({
    atual,
    setAtual,
    slides: carouselVideos ?? [],
  });

  const presenceButtonRule = useMemo(() => {
    if (currentEvent!?.status! === AssembleiaStatus.Live) {
      if (currentEvent!?.contasDisponiveis!?.length!) {
        return true;
      } else if (
        !currentEvent?.contasDisponiveis!?.length! &&
        currentEvent!?.flagMenorSemEmancipado!
      ) {
        return true;
      }
    }

    return false;
  }, [currentEvent]);

  const size = useScreenSize().rankedSize;
  const fixedElement = useMemo(() => {
    const el =
      size <= ScreenSize.sm
        ? document.getElementById(FixedElements.header)
        : document.getElementById(FixedElements.footer);
    return el ?? undefined;
  }, [size]);

  useEffect(() => {
    const handleOrientationChange = () => {
      if (window.screen.orientation) {
        const orientation = window.screen.orientation.type;
        if (
          orientation.includes('landscape') &&
          (window.screen.orientation.angle === 90 ||
            window.screen.orientation.angle === 180)
        ) {
          setIsFullscreen(true);
        } else {
          setIsFullscreen(false);
        }
      } else {
        console.warn("The orientation API isn't supported in this browser.");
      }
    };

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    handleOrientationChange();

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (isFullscreen) {
      const header = document.querySelector(
        '.full-height-container .full-height-container-row-header'
      ) as HTMLDivElement;

      const footer = document.querySelector(
        '.full-height-container-row-footer'
      ) as HTMLDivElement;

      if (header) {
        header.style.zIndex = '-1';
      }

      if (footer) {
        footer.style.zIndex = '-1';
      }
    }
  }, [isFullscreen]);

  return (
    <div className={styles.root}>
      {!loadingEvent &&
      !loadingPautas &&
      currentEvent?.pautas?.length &&
      !naoMostrarBanner &&
      (!conselheiroNaoVota || !conselheiroNaoVota2) &&
      !currentEvent.flagMenorSemEmancipado &&
      !currentEvent.flagPresencaNaoOnline ? (
        <Fragment>
          {fixedElement && titleLabels() && descriptionLabels() ? (
            createPortal(
              <Progress
                title={titleLabels()!}
                subtitle={descriptionLabels()!}
                counter={contadorVotos}
                minValue={countVotos?.length}
                totalValue={votosPermitidos?.length}
              />,
              fixedElement
            )
          ) : (
            <Fragment />
          )}
        </Fragment>
      ) : (
        <Fragment />
      )}
      <Row>
        <Row>
          <Col noPadding cols={[12, 12, 12, 7, 7]}>
            <Row className={styles.video_area}>
              <Row>
                {currentEvent?.urlTransmissao &&
                currentEvent.urlTransmissao &&
                hasPermissionVideos ? (
                  <Col>
                    <div
                      className={styles.player_content}
                      style={
                        isFullscreen
                          ? {
                              position: 'fixed',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              zIndex: 9999999999999999,
                            }
                          : {}
                      }
                    >
                      <VideoPlayer
                        id={'video-assembleia'}
                        key={'video-assembleia'}
                        url={watchToEmbed}
                        autoPlay={true}
                        callBackStarted={(id) => {
                          dispatchToAllYouTubeVideos('pauseVideo', id);
                        }}
                        blockRedirect={true}
                        blackGround={isFullscreen}
                        iframeProps={{
                          allowFullScreen: true,
                        }}
                      />
                    </div>
                  </Col>
                ) : undefined}
              </Row>
              <Row>
                <Col noPadding>
                  <Row>
                    <Col>
                      <Title showBackButton={false}>{currentEvent!.nome}</Title>
                    </Col>
                    <Col>
                      <PageSubtitle
                        subtitle={
                          translation.AssembleiaStatus[
                            `${currentEvent!.status}`
                          ]
                        }
                      />
                      {screenSize >= ScreenSize.lg
                        ? currentEvent?.dataEvento && (
                            <AssembleiaDateTime
                              date={parseISO(currentEvent.dataEvento)}
                            />
                          )
                        : undefined}
                    </Col>
                    {screenSize <= ScreenSize.md ? (
                      <Col>
                        {currentEvent?.dataEvento && (
                          <AssembleiaDateTime
                            date={parseISO(currentEvent.dataEvento)}
                          />
                        )}
                      </Col>
                    ) : undefined}
                  </Row>
                </Col>
                <Row
                  className={clsx([
                    styles.card_content,
                    'assembleia-associado-card-grid',
                  ])}
                >
                  <Col cols={[6, 6, 3, 3, 3]}>
                    {hasPermissionDocumentos && (
                      <LabelValueCard
                        icon={<EvidenciasIcon />}
                        label={
                          translation.AssembleiaAssociadoRoute.infoCard
                            .documentos
                        }
                        value={
                          loadingEvent ? '-' : currentEvent!.documentos.length
                        }
                        onClick={
                          currentEvent!.documentos.length > 0
                            ? abrirDocumentos
                            : undefined
                        }
                        button={currentEvent!.documentos.length > 0}
                        disabled={!Boolean(currentEvent!.documentos.length > 0)}
                      />
                    )}
                  </Col>
                  <Col cols={[6, 6, 3, 3, 3]}>
                    {currentEvent?.contasDisponiveis &&
                    screenSize > ScreenSize.sm ? (
                      <LinkScroll
                        to={
                          currentEvent?.videos &&
                          currentEvent?.videos.length >= 1
                            ? 'videos-section'
                            : ''
                        }
                        containerId={'root-container-custom'}
                        spy={true}
                        smooth={'easeInOutQuart'}
                        offset={0}
                        duration={1000}
                      >
                        <LabelValueCard
                          icon={<PlayIcon />}
                          label={
                            translation.AssembleiaAssociadoRoute.infoCard.videos
                          }
                          value={currentEvent!.videos.length}
                          button={
                            currentEvent?.videos &&
                            currentEvent?.videos.length >= 1
                              ? true
                              : false
                          }
                          disabled={
                            currentEvent?.videos &&
                            currentEvent?.videos.length === 0
                          }
                        />
                      </LinkScroll>
                    ) : (
                      <LabelValueCard
                        icon={<PlayIcon />}
                        label={
                          translation.AssembleiaAssociadoRoute.infoCard.videos
                        }
                        value={currentEvent!.videos.length}
                        button={
                          currentEvent?.videos &&
                          currentEvent?.videos.length >= 1
                            ? true
                            : false
                        }
                        disabled={
                          currentEvent?.videos &&
                          currentEvent?.videos.length === 0
                        }
                        onClick={() =>
                          currentEvent!.videos &&
                          currentEvent!.videos.length >= 1
                            ? abrirVideos()
                            : undefined
                        }
                      />
                    )}
                  </Col>
                  <Col cols={[6, 6, 3, 3, 3]}>
                    <LabelValueCard
                      icon={<ComunidadeIcon />}
                      label={
                        translation.AssembleiaAssociadoRoute.infoCard.presenca
                      }
                      value={contadorContas}
                      onClick={
                        currentEvent!?.status === AssembleiaStatus.Live
                          ? currentEvent!?.flagMenorSemEmancipado ||
                            (currentEvent!?.flagPresencaNaoOnline &&
                              currentEvent!?.flagMenorSemEmancipado)
                            ? abrirModalMenorEmancipado
                            : !currentEvent!?.flagMenorSemEmancipado &&
                              !currentEvent!?.flagPresencaNaoOnline
                            ? abrirModalContas
                            : abrirModalContasConfirmadas
                          : undefined
                      }
                      button={currentEvent!?.status === AssembleiaStatus.Live}
                      loading={currentEvent?.needLoadContas}
                      disabled={!presenceButtonRule}
                    />
                  </Col>
                  <Col cols={[6, 6, 3, 3, 3]}>
                    <LinkScroll
                      to={'pautas'}
                      containerId={'root-container-custom'}
                      spy={true}
                      smooth={'easeInOutQuart'}
                      offset={-180}
                      duration={1000}
                    >
                      <LabelValueCard
                        icon={<ListIcon />}
                        label={
                          translation.AssembleiaAssociadoRoute.infoCard.pautas
                        }
                        value={
                          currentEvent?.pautas && currentEvent?.pautas.length
                        }
                        button
                        loading={currentEvent?.needLoadPautas}
                      />
                    </LinkScroll>
                  </Col>
                </Row>
              </Row>
            </Row>
          </Col>
          <Col noPadding cols={[12, 12, 12, 5, 5]}>
            <Row>
              <Col>
                {hasPermissionDuvidas &&
                  window.matchMedia('(min-width: 960px)').matches && (
                    <DuvidasFrequentes
                      evento={currentEvent!}
                      associado={usuario!}
                    />
                  )}
              </Col>
            </Row>
          </Col>
          {hasPermissionDuvidas &&
            window.matchMedia('(max-width: 959px)').matches && (
              <BotaoFlutuante icon={<ChatIcon />} />
            )}
          {isColaborador && (
            <div className={styles.adivice_colaborador}>
              <TipoConta />
              <p>
                {translation.SeletorContasParaVotoModal.textPessoaColaborador}
              </p>
            </div>
          )}
        </Row>
        {carouselVideos.length ? (
          <Row id={'videos-section'} className={styles.video_carousel}>
            {carouselVideos.length && screenSize >= ScreenSize.md ? (
              <Fragment>
                <Col>
                  <Title>Vídeos</Title>
                </Col>
                <Col className={styles.videos_container} gap={24}>
                  {CarouselBehaviour}
                </Col>
                <Row>
                  {carouselVideos.length > 1 ? (
                    <Col centralized>
                      <ElementPaginator
                        infinite
                        size={carouselVideos.length! || 0}
                        atual={atual + 1}
                        onChange={(newAtual) => {
                          handleChange(newAtual - 1, false);
                        }}
                      />
                    </Col>
                  ) : undefined}
                </Row>
              </Fragment>
            ) : (
              CarouselBehaviour
            )}
          </Row>
        ) : (
          CarouselBehaviour
        )}
      </Row>
    </div>
  );
}
